import React from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useCheckoutPersistence } from '../hooks/useCheckoutPersistence';
import { useLocation } from 'react-router-dom';
import { useSubscriptionAccess } from '@company/hooks/useSubscriptionAccess';

interface CheckoutResumePromptProps {
  hasRestoredState: boolean;
}

/**
 * Komponente zur Anzeige einer Benachrichtigung wenn der Checkout-Zustand wiederhergestellt wurde.
 * 
 * Funktionalität:
 * - Zeigt eine freundliche Nachricht wenn Produktauswahl wiederhergestellt wurde
 * - Bietet Option zum Löschen der gespeicherten Auswahl
 * - Verschwindet automatisch nach Benutzerinteraktion
 * - Nicht-aufdringlich und benutzerfreundlich
 */
const CheckoutResumePrompt: React.FC<CheckoutResumePromptProps> = ({ hasRestoredState }) => {
  const { clearSavedState } = useCheckoutPersistence();
  const location = useLocation();
  const [isVisible, setIsVisible] = React.useState(hasRestoredState);
  const { hasAccess, loading: subscriptionLoading } = useSubscriptionAccess();

  // Prüfe ob Benutzer von automatischer Weiterleitung kommt
  const isFromRedirect = location.state?.fromCheckoutRedirect;
  const isFromDashboard = location.state?.fromDashboardBanner;
  const restoredProductCount = location.state?.restoredProductCount;

  // Aktualisiere Sichtbarkeit wenn sich hasRestoredState ändert
  React.useEffect(() => {
    setIsVisible(hasRestoredState);
  }, [hasRestoredState]);

  // Verstecke Komponente wenn nicht relevant
  if (!isVisible || subscriptionLoading || hasAccess) {
    return null;
  }

  const handleDismiss = () => {
    setIsVisible(false);
  };

  const handleClearAndDismiss = async () => {
    await clearSavedState();
    setIsVisible(false);
  };

  // Bestimme Nachrichtentext basierend auf Kontext
  const getAlertContent = () => {
    if (isFromRedirect && restoredProductCount) {
      return {
        title: "Willkommen zurück!",
        message: `Du wurdest automatisch zu deinem unterbrochenen Checkout weitergeleitet. ${restoredProductCount} Produkt${restoredProductCount > 1 ? 'e wurden' : ' wurde'} wiederhergestellt.`,
        severity: "success" as const
      };
    } else if (isFromDashboard && restoredProductCount) {
      return {
        title: "Checkout fortgesetzt",
        message: `Perfekt! Deine ${restoredProductCount} ausgewählten Produkte sind bereit für den Checkout.`,
        severity: "success" as const
      };
    } else {
      return {
        title: "Auswahl wiederhergestellt",
        message: "Deine vorherige Produktauswahl wurde automatisch wiederhergestellt.",
        severity: "info" as const
      };
    }
  };

  const { title, message, severity } = getAlertContent();

  return (
    <Alert
      severity={severity}
      onClose={handleDismiss}
      sx={{ mb: 2 }}
      action={
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleClearAndDismiss}
          >
            Auswahl löschen
          </Button>
          <Button size="small" onClick={handleDismiss}>
            OK
          </Button>
        </Stack>
      }
    >
      <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2">{message}</Typography>
    </Alert>
  );
};

export default CheckoutResumePrompt; 